import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { createStockIssue } from '../../redux/actions/stockIssue/stockIssueAction';
import { fetchRawStocks } from '../../redux/actions/rawStock/rawStockAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import { fetchImportedInventory } from '../../redux/actions/importedInventory/importedInventoryAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { showError, showSuccess } from '../../utils/alertUtils';

const initialForm = {
  emp_code: '',
  batch_no: '',
  stock_code: '',
  stock_name: '',
  stock_type: '',
  karatage: '',
  color: '',
  stone_type: '',
  qty: '',
  weight: '',
};

const StockIssueForm = ({ onReset, onSuccess }) => {
  const dispatch = useDispatch();
  const { rawStocks } = useSelector((state) => state.rawStock);
  const { employees } = useSelector((state) => state.employee);
  const { importedInventory } = useSelector((state) => state.importedInventory);
  const { purities } = useSelector((state) => state.purity);
  const user = useSelector((state) => state.login.user);
  const [formData, setFormData] = useState(initialForm);
  const [selectedStock, setSelectedStock] = useState(null);
  const [purityWarning, setPurityWarning] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchRawStocks({ limit: 0, today_only: false }));
    dispatch(fetchEmployees());
    dispatch(fetchImportedInventory());
    dispatch(fetchPurities());
  }, [dispatch]);

  // Only show stock codes with qty > 0 and weight > 0
  // const availableStocks = rawStocks.filter(s => s.qty > 0 && s.weight > 0);
  const availableStocks = rawStocks;

  const handleStockCodeChange = (event, value) => {
    setSelectedStock(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        stock_code: value.stock_code,
        stock_name: value.stock_name,
        stock_type: value.stock_type_name,
        karatage: value.karatage_name,
        color: value.color_name,
        stone_type: value.stone_type_name,
      }));
    } else {
      setFormData(initialForm);
    }
  };

  // Filter out completed batches (status 2) from imported inventory
  const availableBatches = importedInventory.filter(batch => 
    batch.batch_no_status === 0 || batch.batch_no_status === 1
  );

  // Debug logging
  useEffect(() => {
    console.log('All imported inventory:', importedInventory);
    console.log('Available batches (filtered):', availableBatches);
    console.log('Batch status counts:', {
      pending: importedInventory.filter(b => b.batch_no_status === 0).length,
      inProgress: importedInventory.filter(b => b.batch_no_status === 1).length,
      completed: importedInventory.filter(b => b.batch_no_status === 2).length
    });
  }, [importedInventory, availableBatches]);

  // When batch_no changes, set karatage from importedInventory and purities
  useEffect(() => {
    if (formData.batch_no) {
      const batch = importedInventory.find(b => b.batch_number === formData.batch_no);
      if (batch && batch.hm_purity && purities.length > 0) {
        console.log('Batch:', batch);
        console.log('Purities:', purities);
        const batchPurity = Number(Number(batch.hm_purity).toFixed(2));
        const purity = purities.find(p => Math.abs(Number(Number(p.hm_purity).toFixed(2)) - batchPurity) < 0.01);
        console.log('Matching purity:', purity);
        setFormData(prev => ({
          ...prev,
          karatage: purity ? purity.name : '',
        }));
        setPurityWarning(purity ? '' : `No matching purity found for HM Purity ${batch.hm_purity}`);
      } else {
        setFormData(prev => ({ ...prev, karatage: '' }));
        setPurityWarning('No matching purity found or purities not loaded.');
      }
    } else {
      setPurityWarning('');
    }
  }, [formData.batch_no, importedInventory, purities]);

  // Find the selected batch and its status
  const selectedBatch = importedInventory.find(b => b.batch_number === formData.batch_no);
  const isCompletedBatch = selectedBatch && selectedBatch.batch_no_status === 2;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Validation: Only one of emp_code or batch_no must be filled
    if ((formData.emp_code && formData.batch_no) || (!formData.emp_code && !formData.batch_no)) {
      showError('Validation Error', 'Select either Employee or Batch No, not both.');
      setLoading(false);
      return;
    }
    if (!formData.stock_code) {
      showError('Validation Error', 'Stock Code is required.');
      setLoading(false);
      return;
    }
    if (!formData.qty || isNaN(formData.qty) || Number(formData.qty) <= 0) {
      showError('Validation Error', 'Quantity must be a positive number.');
      setLoading(false);
      return;
    }
    if (!formData.weight || isNaN(formData.weight) || Number(formData.weight) <= 0) {
      showError('Validation Error', 'Weight must be a positive number.');
      setLoading(false);
      return;
    }
    // Check if enough stock is available
    if (selectedStock && (Number(formData.qty) > selectedStock.qty || Number(formData.weight) > selectedStock.weight)) {
      showError('Validation Error', 'Insufficient stock available.');
      setLoading(false);
      return;
    }
    // Karatage validation for batch-based issue
    if (formData.batch_no && !formData.emp_code) {
      // Find the batch and its karatage
      const batch = importedInventory.find(b => b.batch_number === formData.batch_no);
      let batchKaratage = '';
      if (batch && batch.hm_purity && purities.length > 0) {
        const purityObj = purities.find(p => Math.abs(Number(Number(p.hm_purity).toFixed(2)) - Number(Number(batch.hm_purity).toFixed(2))) < 0.01);
        batchKaratage = purityObj ? purityObj.name : '';
      }
      const stockKaratage = selectedStock?.karatage_name || '';
      if (batchKaratage && stockKaratage && batchKaratage !== stockKaratage) {
        showError('Karatage Mismatch', `Batch karatage (${batchKaratage}) and selected stock karatage (${stockKaratage}) do not match. Cannot issue stock for this batch.`);
        setLoading(false);
        return;
      }
    }
    try {
      console.log('User object:', user);
      console.log('Username:', user?.username);
      
      const username = user?.username || 'admin'; // Fallback to 'admin' if username is not available
      console.log('Using username:', username);
      
      const stockIssueData = {
        ...formData,
        qty: Number(formData.qty),
        weight: Number(formData.weight),
        created_by: username
      };
      
      console.log('Stock issue data being sent:', stockIssueData);
      
      await dispatch(createStockIssue(stockIssueData));
      //console.log(formData);
      showSuccess('Success', 'Stock issued successfully.');
      setFormData(initialForm);
      setSelectedStock(null);
      if (onReset) onReset();
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to issue stock.';
      showError('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Stock Issue
      </Typography>
      {isCompletedBatch && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error" variant="body1">
            This batch is completed. Stock issue is not allowed.
          </Typography>
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Autocomplete
            options={employees}
            getOptionLabel={option => option ? `${option.emp_code} - ${option.name}` : ''}
            value={employees.find(emp => emp.emp_code === formData.emp_code) || null}
            onChange={(_, value) => {
              setFormData(prev => ({ ...prev, emp_code: value ? value.emp_code : '', batch_no: '' }));
              // If employee is selected, reset karatage to be set by stock code
              if (value && selectedStock) {
                setFormData(prev => ({
                  ...prev,
                  karatage: selectedStock.karatage_name || '',
                }));
              }
            }}
            disabled={!!formData.batch_no}
            renderInput={(params) => (
              <TextField {...params} label="Employee (search)" variant="outlined" />
            )}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            options={availableBatches}
            getOptionLabel={option => option ? option.batch_number : ''}
            value={availableBatches.find(batch => batch.batch_number === formData.batch_no) || null}
            onChange={(_, value) => {
              setFormData(prev => ({ ...prev, batch_no: value ? value.batch_number : '', emp_code: '' }));
              // When batch is selected, karatage will be set by useEffect above
            }}
            disabled={!!formData.emp_code}
            renderInput={(params) => (
              <TextField {...params} label="Batch No (search)" variant="outlined" />
            )}
            sx={{ minWidth: 200 }}
          />
        </Box>
        <Autocomplete
          options={availableStocks}
          getOptionLabel={option => `${option.stock_code} - ${option.stock_name}`}
          value={selectedStock}
          onChange={handleStockCodeChange}
          renderInput={(params) => <TextField {...params} label="Stock Code" required />}
          sx={{ mb: 2, minWidth: 300 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Stock Name" value={formData.stock_name} InputProps={{ readOnly: true }} fullWidth />
          <TextField label="Stock Type" value={formData.stock_type} InputProps={{ readOnly: true }} fullWidth />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Karatage" value={formData.karatage} InputProps={{ readOnly: true }} fullWidth sx={{ display: formData.stock_type?.toLowerCase() === 'gold' ? 'block' : 'none' }} />
          {purityWarning && (
            <Typography color="warning.main" sx={{ fontWeight: 'bold', fontSize: 13, mt: 1 }}>
              {purityWarning}
            </Typography>
          )}
          <TextField label="Color" value={formData.color} InputProps={{ readOnly: true }} fullWidth />
          <TextField label="Stone Type" value={formData.stone_type} InputProps={{ readOnly: true }} fullWidth sx={{ display: formData.stock_type?.toLowerCase() === 'gold' ? 'none' : 'block' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Quantity"
            name="qty"
            value={formData.qty}
            onChange={handleChange}
            type="number"
            required
            fullWidth
          />
          <TextField
            label="Weight"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            type="number"
            required
            fullWidth
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading || isCompletedBatch}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Submitting...' : 'Issue Stock'}
        </Button>
      </form>
    </Paper>
  );
};

export default StockIssueForm; 